import { VCPM } from '../models/project.model';
import { PcsSizingEngineRequest } from '../models/pcs-sizing-engine-request.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import { AppConstants } from '../models/app-constants.model';
import {
  getManagementDomainRecommendationRequest,
  getWorkloadDomainRecommendationRequest,
  SizingRequestContext,
} from './sizing-request.adapter';
import { withoutProfileTierComparison } from './sizing-recalculation.adapter';

/**
 * Which cluster group is being stepped, and how. A cluster group is one cluster and every workload that
 * shares it; the engine sizes them as one cluster, so a step moves them together.
 */
export interface LogicBoundStep {
  requirementsId: string;
  /** The cluster as its chassis name it - the key the engine groups workloads into one cluster by. */
  clusterName: string;
  /** Which chassis of each workload is the group's: a vSAN Max workload has one per side. */
  clusterType: PcsSizingEngineRequest.ClusterType;
  /** The client's id for the cluster, for the engine's logs only - never used to find it. */
  clusterId?: string;
  /** The tier the rendered recommendation came from; scopes the ladder to that tier's ends. */
  profileName?: string;
  /** Omitted for a node-count-only change, which carries minNodes alone. */
  operand?: PcsSizingEngineRequest.Operand;
  operation?: PcsSizingEngineRequest.Operation;
  /** A floor on host count, never an override. */
  minNodes?: number;
}

/**
 * The management domain's one cluster, named exactly as its sizing request names it: the management
 * requirement, whose only workload's chassis is the HCI cluster 'Management Domain'. A step carrying it is
 * therefore a management-domain step end to end - the request is built from the management domain and the
 * engine sizes it as one (domain type MANAGEMENT, with that domain's host floor).
 */
export const MANAGEMENT_LOGIC_BOUND_TARGET: Pick<LogicBoundStep, 'requirementsId' | 'clusterName' | 'clusterType'> = {
  requirementsId: AppConstants.MANAGEMENT_DOMAIN_REQUIREMENT_ID,
  clusterName: 'Management Domain',
  clusterType: 'HCI',
};

/** A Logic Bound request, and the workloads of the cluster group whose answers its response carries. */
export interface LogicBoundGroupRequest {
  request: PcsSizingEngineRequest.LogicBoundRequest;
  memberWorkloadIds: string[];
}

/**
 * Builds the request for one Logic Bound step on a cluster group.
 *
 * Reuses the domain's own recommendation request builder unchanged - getManagementDomainRecommendationRequest()
 * for the management requirement, getWorkloadDomainRecommendationRequest() otherwise - so a step is sized
 * from exactly the same payload as an ordinary recommendation, and every other workload travels along with
 * its real configuration. That is not incidental: workload index 0 is sized against the aggregated host
 * and VM counts of its siblings, so a request narrowed to the one workload would get a different answer.
 *
 * Two things are then substituted into every workload of the cluster group, and nothing else:
 *
 * The rendered hardware replaces the group's chassis hardware. What the user is stepping is the hardware
 * currently ON SCREEN, which for a no-preference cluster is not what was sent as input - the input carried
 * no hardware at all and the tier was chosen by the engine. Reading it back out of the recommendation is
 * the only way the step starts from what the user can see. Only the hardware moves: each chassis keeps
 * its own identity (id, cluster name and type, ownership), and a vSAN Max workload's other side is left
 * alone. Every member gets it, because the engine sizes the group from whichever member it keeps.
 *
 * And the side's preference is forced to HARDWARE_FIRST, the same trick
 * forceHardwareFirstRecalculationConfig() uses: a chassis is concrete, so leaving the workload on
 * NO_PREFERENCE would re-run tier selection and hand back whichever tier the engine preferred,
 * discarding the step the user just asked for.
 *
 * The built request is a fresh object graph, so - unlike the recalculation adapter, which mutates a
 * project clone before building - nothing here needs cloning: the project is never touched.
 */
export function getLogicBoundRequest(
  project: VCPM.Project,
  renderedChassis: PcsSizingEngineResponse.Chassis | undefined | null,
  step: LogicBoundStep,
  context: SizingRequestContext,
): LogicBoundGroupRequest | null {
  const designRequest = withoutProfileTierComparison(
    step.requirementsId === AppConstants.MANAGEMENT_DOMAIN_REQUIREMENT_ID
      ? getManagementDomainRecommendationRequest(project, context)
      : getWorkloadDomainRecommendationRequest(project, context),
  );
  if (!designRequest) return null;

  const members = findClusterGroup(designRequest.design, step);
  if (!members.length) return null;

  // A resource step needs something to step. A node-count-only change does not - it moves a floor on the
  // cluster and leaves the hardware exactly as the engine chose it.
  const isResourceStep = !!step.operand;
  if (!renderedChassis) {
    if (isResourceStep) return null;
  } else {
    // The response Chassis and the request Chassis are the same wire object, serialized by the engine
    // and handed straight back to it. The two TypeScript interfaces differ only because the request
    // model was hand-written with looser scalar types for the value/unit pairs.
    const rendered = renderedChassis as unknown as PcsSizingEngineRequest.Chassis;
    members.forEach(({ workload, chassis }) => {
      chassis.part = rendered.part;
      chassis.readynode = rendered.readynode;
      chassis.components = rendered.components;
      if (step.clusterType === 'SON') {
        workload.storageHardwarePreference = 'HARDWARE_FIRST';
      } else {
        workload.hardwarePreference = 'HARDWARE_FIRST';
      }
    });
  }

  return {
    request: {
      design: designRequest.design,
      requirementsId: step.requirementsId,
      clusterName: step.clusterName,
      clusterType: step.clusterType,
      clusterId: step.clusterId,
      profileName: step.profileName,
      operand: step.operand,
      operation: step.operation,
      minNodes: step.minNodes,
    },
    memberWorkloadIds: [...new Set(members.map(({ workload }) => workload.workloadId))],
  };
}

const OPERAND_LABELS: Record<PcsSizingEngineRequest.Operand, string> = {
  CPU: 'CPU',
  IOPS: 'IOPS',
  STORAGE: 'Storage',
  MEMORY: 'Memory',
};

/**
 * Why a step was refused, phrased for the panel - null when it was applied or nothing is known yet.
 *
 * A refusal arrives as an ordinary 200 with applied:false. Since a control the engine has said nothing
 * about is offered rather than disabled, the first press on an exhausted operand genuinely lands here,
 * and saying nothing would read as the control being broken. Shared so every page says the same thing.
 */
export function describeLogicBoundRefusal(
  logicBound: PcsSizingEngineResponse.LogicBoundState | undefined | null,
): string | null {
  if (!logicBound || logicBound.applied) {
    return null;
  }
  const resource = logicBound.operand ? OPERAND_LABELS[logicBound.operand] : null;
  switch (logicBound.blockedReason) {
    case 'AT_LADDER_FLOOR':
      return `${resource ?? 'This resource'} is already at the smallest size available for this hardware.`;
    case 'AT_LADDER_CEILING':
      return `${resource ?? 'This resource'} is already at the largest size available for this hardware.`;
    case 'OPERAND_NOT_SUPPORTED':
      return `${resource ?? 'This resource'} cannot be adjusted for this configuration.`;
    default:
      return 'That change could not be applied to this configuration.';
  }
}

/**
 * The chassis currently rendered for a workload - the hardware the user is looking at, which is what a
 * step starts from. Read off the first cluster, which is the one the Resource Usage panel reports.
 */
export function getRenderedChassis(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation | undefined | null,
): PcsSizingEngineResponse.Chassis | undefined {
  return recomm?.clusters?.[0]?.serverConfiguration?.chassis;
}

/**
 * Writes a stepped chassis back into the hardware configuration it was stepped from, so a Logic Bound
 * step is also a customization: the Recalculate panel re-seeds from it, and Save Changes persists it
 * through the same path a Recalculate uses.
 *
 * The exact inverse of buildHardwareConfigurationPayload for the fields a step can move - cores and
 * sockets, clock speed, RAM (GB), drive count, drive size (TB), network speed. Everything else
 * (ReadyNode selection, search filters, OSA disk-group fields) is carried over untouched. A field the
 * chassis does not state keeps its previous value rather than being blanked, and a drive size is written
 * as a concrete size, never 'other', since the chassis always carries one.
 *
 * Returns a fresh object; `base` is not mutated.
 */
export function applyChassisToHardwareConfiguration(
  base: VCPM.HardwareConfiguration,
  chassis: PcsSizingEngineResponse.Chassis | undefined | null,
): VCPM.HardwareConfiguration {
  const next: VCPM.HardwareConfiguration = JSON.parse(JSON.stringify(base));
  const common = { ...(next.commonConfig ?? {}) } as VCPM.HardwareConfiguration['commonConfig'];
  const cpu = chassis?.components?.cpu?.available?.[0];
  const memory = chassis?.components?.memory?.available?.[0];
  const storage = chassis?.components?.storage?.available?.[0];

  assign(common, 'cores', readNumber(cpu?.cores));
  assign(common, 'sockets', readNumber(cpu?.sockets));
  assign(common, 'clockSpeed', readNumber(cpu?.speed));
  assign(common, 'networkSpeed', readNumber(cpu?.networkSpeed));
  assign(common, 'serverRam', readScaled(memory?.serverRam, BYTES_IN['GB']));
  assign(common, 'numberOfDrives', readNumber(storage?.maxDriveSlots));
  const driveSize = readScaled(storage?.capacityDriveSize, BYTES_IN['TB']);
  if (driveSize !== undefined) {
    common.driveSize = driveSize;
    delete common.customDriveSize;
  }

  next.commonConfig = common;
  return next;
}

const BYTES_IN: Record<string, number> = { B: 1, KB: 1e3, MB: 1e6, GB: 1e9, TB: 1e12, PB: 1e15 };

// The engine serializes a chassis quantity as {value, units}; a chassis the client built itself (see
// buildHardwareConfigurationPayload) carries bare numbers. Both are read here.
type Quantity = number | string | { value?: number | string; unit?: string; units?: string } | null | undefined;

function readNumber(quantity: Quantity): number | undefined {
  const raw = typeof quantity === 'object' && quantity !== null ? quantity.value : quantity;
  const n = Number(raw);
  return raw === undefined || raw === null || raw === '' || !Number.isFinite(n) ? undefined : n;
}

/** Reads a byte quantity in the unit `target` is expressed in; a bare number is taken as already in it. */
function readScaled(quantity: Quantity, target: number): number | undefined {
  const value = readNumber(quantity);
  if (value === undefined) return undefined;
  const unit = typeof quantity === 'object' && quantity !== null ? (quantity.units ?? quantity.unit) : undefined;
  const factor = unit ? BYTES_IN[unit.toUpperCase()] : undefined;
  return factor ? Math.round((value * factor) / target * 1e6) / 1e6 : value;
}

function assign<K extends keyof VCPM.HardwareConfiguration['commonConfig']>(
  common: VCPM.HardwareConfiguration['commonConfig'],
  field: K,
  value: number | undefined,
): void {
  if (value !== undefined) {
    common[field] = value as VCPM.HardwareConfiguration['commonConfig'][K];
  }
}

/**
 * Every chassis of the cluster group in the requirement, with the workload carrying it - matched the way
 * the engine matches a step: by name and by side, a chassis without a side being HCI.
 */
function findClusterGroup(
  design: PcsSizingEngineRequest.Design,
  step: LogicBoundStep,
): { workload: PcsSizingEngineRequest.Workload; chassis: PcsSizingEngineRequest.Chassis }[] {
  const workloads = design.requirements?.find((r) => String(r.requirementsId) === String(step.requirementsId))?.workloads ?? [];
  return workloads.flatMap((workload) => (workload.chassis ?? [])
    .filter((chassis) => chassis.clusterName === step.clusterName && (chassis.clusterType ?? 'HCI') === step.clusterType)
    .map((chassis) => ({ workload, chassis })));
}
